if (!customElements.get('tool-signup')) {
  customElements.define(
    'tool-signup',
    class ToolSignup extends HTMLElement {
      connectedCallback() {
        this.tools = Array.from(this.querySelectorAll('[data-tool]'));
        this.platforms = Array.from(this.querySelectorAll('[data-platform]'));
        this.customization = this.querySelector('[data-customization]');
        this.setupTotal = this.querySelector('[data-setup-total]');
        this.subscriptionTotal = this.querySelector('[data-subscription-total]');
        this.hint = this.querySelector('[data-signup-hint]');
        this.selectButton = this.querySelector('[data-tool-select]');
        this.dialog = this.querySelector('[data-package-dialog]');
        this.confirmButton = this.dialog.querySelector('[data-package-confirm]');
        this.lines = [];

        this.tools.forEach((input) => input.addEventListener('change', () => this.onToolChange(input)));
        this.platforms.forEach((input) => input.addEventListener('change', () => this.update()));
        this.selectButton.addEventListener('click', () => this.openDialog());
        this.confirmButton.addEventListener('click', () => this.onConfirm());
        this.dialog.querySelectorAll('[data-package-cancel]').forEach((button) =>
          button.addEventListener('click', () => this.dialog.close())
        );
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });

        this.update();
      }

      // Monthly and annual billing of the same tool are alternatives, so checking one clears the other.
      onToolChange(input) {
        if (input.checked) {
          this.tools.forEach((other) => {
            if (other !== input && other.dataset.group === input.dataset.group) other.checked = false;
          });
        }
        this.update();
      }

      getTools() {
        return this.tools.filter((input) => input.checked);
      }

      getPlatform() {
        return this.platforms.find((input) => input.checked);
      }

      formatMoney(cents) {
        const digits = cents % 100 === 0 ? 0 : 2;
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }).format(cents / 100);
      }

      update() {
        const tools = this.getTools();

        if (this.setupTotal) {
          const setupCents = tools.reduce((sum, input) => sum + Number(input.dataset.onboardingCents || 0), 0);
          this.setupTotal.textContent = tools.length ? this.formatMoney(setupCents) : '';
        }

        // One total per billing period, e.g. "$175/Month + $2,950/Year".
        const periods = new Map();
        tools.forEach(({ dataset }) =>
          periods.set(dataset.priceSuffix, (periods.get(dataset.priceSuffix) || 0) + Number(dataset.priceCents))
        );
        this.subscriptionTotal.textContent = Array.from(
          periods,
          ([suffix, cents]) => `${this.formatMoney(cents)}${suffix}`
        ).join(' + ');

        const ready = tools.length > 0 && (!this.platforms.length || Boolean(this.getPlatform()));
        this.selectButton.disabled = !ready;
        if (this.hint) this.hint.hidden = ready;
      }

      // Lines with an item go to checkout. The rest are only listed in the popup and saved as properties
      // on the first item, since a cart permalink applies its properties to the first line item.
      getLines() {
        const properties = {};
        const lines = [];

        this.getTools().forEach(({ value, dataset }) => {
          lines.push({
            item: { id: Number(value), quantity: 1 },
            title: dataset.title,
            price: dataset.price + dataset.priceSuffix,
          });
          if (dataset.onboardingId) {
            lines.push({
              item: { id: Number(dataset.onboardingId), quantity: 1 },
              title: dataset.onboardingTitle,
              price: dataset.onboardingPrice,
            });
          }
        });

        if (this.customization?.checked) {
          const { variantId, label, priceText } = this.customization.dataset;
          if (variantId) {
            lines.push({ item: { id: Number(variantId), quantity: 1 }, title: label, price: priceText });
          } else {
            properties[label] = priceText;
            lines.push({ title: label, price: priceText });
          }
        }

        const platform = this.getPlatform();
        if (platform) {
          properties[this.dataset.platformLabel] = platform.value;
          lines.push({ title: this.dataset.platformLabel, price: platform.value });
        }

        this.querySelectorAll('[data-signup-property]').forEach((input) => {
          const value = input.value.trim();
          if (value) properties[input.name] = value;
        });
        if (Object.keys(properties).length) lines[0].item.properties = properties;

        return lines;
      }

      openDialog() {
        const tools = this.getTools();
        if (!tools.length) return;

        this.lines = this.getLines();

        const message = this.dialog.querySelector('[data-package-message]');
        message.textContent = message.dataset.template.replace(
          '[tools]',
          tools.map(({ dataset }) => dataset.title).join(' & ')
        );
        this.dialog.querySelector('[data-package-lines]').replaceChildren(
          ...this.lines.map(({ title, price }) => {
            const line = document.createElement('li');
            const name = document.createElement('span');
            const amount = document.createElement('strong');
            name.textContent = title;
            amount.textContent = price;
            line.append(name, amount);
            return line;
          })
        );

        this.dialog.showModal();
      }

      // A cart permalink goes straight to checkout with only these items, skipping the cart page.
      onConfirm() {
        const items = this.lines.filter(({ item }) => item).map(({ item }) => item);
        if (!items.length) return;

        const variants = items.map(({ id, quantity }) => `${id}:${quantity}`).join(',');
        const { properties } = items[0];
        let url = `${window.routes.cart_url}/${variants}`;
        if (properties) {
          const json = new TextEncoder().encode(JSON.stringify(properties));
          const encoded = btoa(String.fromCharCode(...json)).replace(/\+/g, '-').replace(/\//g, '_');
          url += `?properties=${encodeURIComponent(encoded)}`;
        }

        window.location.assign(url);
      }
    }
  );
}
